<template>
  <v-container id="user-notification-settings">
    <div class="mb-7">
      <h3>Notification Settings</h3>
      <div class="caption">
        Receive alerts via:
      </div>
    </div>
    <div class="mb-8">
      <setting-tooltip :enabled="!settingsEnabled">
        <template v-slot:activator="{ on, attrs }">
          <span
            v-bind="attrs"
            v-on="on"
          >
            <toggle
              :disabled="!settingsEnabled"
              :value="globalReceiveEmail"
              @toggle="value => handleNotificationSettingsChange({updated: 'globalReceiveEmail', value: value || false})"
            >
              <template #label>
                <h6 class="toggle-label">
                  Email Notifications
                </h6>
              </template>
            </toggle>
            <v-text-field
              class="email-address-field my-4"
              filled
              dense
              readonly
              disabled
              :value="savedUserData.email"
              label="Email Address"
            />
          </span>
        </template>
        <template v-if="!settingsEnabled">
          <span>
            This field cannot be edited because this user belongs to multiple organizations
          </span>
        </template>
      </setting-tooltip>
    </div>
    <div>
      <setting-tooltip :enabled="noSavedPhone || phoneErrors || !settingsEnabled">
        <template v-slot:activator="{ on, attrs }">
          <span
            v-bind="attrs"
            v-on="on"
            @click="checkSMS()"
          >
            <toggle
              :value="globalReceiveSMS && !receiveSMSDisabled"
              :disabled="receiveSMSDisabled || !settingsEnabled"
              @toggle="value => handleNotificationSettingsChange({updated: 'globalReceiveSMS', value: value || false})"
            >
              <template #label>
                <h6 class="toggle-label">
                  SMS Text Messaging
                </h6>
              </template>
            </toggle>
          </span>
        </template>
        <template v-if="!settingsEnabled">
          <span>
            This field cannot be edited because this user belongs to multiple organizations
          </span>
        </template>
        <template v-else>
          <span v-if="noSavedPhone">
            Please set a phone number to enable SMS alert delivery
          </span>
          <span v-else-if="phoneErrors">
            Please set a valid phone number to enable SMS alert delivery
          </span>
        </template>
      </setting-tooltip>
      <div class="d-flex">
        <vue-tel-input-vuetify
          ref="phone"
          :value="phone"
          max-height="52"
          filled
          label="Phone Number"
          :disabled="!settingsEnabled"
          :error-messages="phoneErrors"
          :validate-on-blur="true"
          style="width: 322px; margin-top: 16px;"
          default-country="US"
          @input="handlePhoneInput"
          @blur="handlePhoneBlur"
          @country-changed="updateCountry"
        />
        <transition name="fade">
          <div
            v-if="showPhoneTip"
            class="phone-tip-container"
          >
            <v-icon style="color: darkgreen; margin-right:5px;">
              $faArrowCircleLeft
            </v-icon>
            <span
              class="ml-1"
              style="line-height: 16px"
            >
              Please enter your phone number to enable SMS alerts
            </span>
          </div>
        </transition>

        <v-icon
          v-if="showPhoneCheck"
          color="#456e6c"
          class="ml-2"
        >
          $faCheckCircle
        </v-icon>
      </div>
    </div>
    <setting-tooltip :enabled="!settingsEnabled">
      <template v-slot:activator="{ on, attrs }">
        <div
          v-bind="attrs"
          v-on="on"
        >
          <toggle
            :disabled="!settingsEnabled"
            :value="globalReceivePush"
            @toggle="value => handleNotificationSettingsChange({updated: 'globalReceivePush', value: value || false})"
          >
            <template #label>
              <h6 class="toggle-label">
                Mobile Push Notifications
              </h6>
            </template>
          </toggle>
        </div>
      </template>
      <template>
        <span v-if="!settingsEnabled">
          This field cannot be edited because this user belongs to multiple organizations
        </span>
      </template>
    </setting-tooltip>
    <v-divider class="mt-5" />
    <div class="pa-0">
      <h3 class="mt-6 mb-3">
        Time Zone
      </h3>
      <v-select
        v-model="timezone"
        :placeholder="timezone.text"
        filled
        style="width: 322px;"
        label="Timezone"
        :menu-props="{ bottom: true, offsetY: true }"
        return-object
        item-text="text"
        item-value="value"
        :items="timezones"
        @change="value => updateUserTimezone(value)"
      />
    </div>
  </v-container>
</template>

<script>
  import Toggle from '@/components/base/Toggle'
  import SettingTooltip from '@/components/user/SettingTooltip'
  import { mapGetters } from 'vuex'
  import validationAxios from '@/helpers/validation-client'
  import { getTimeZones } from '@vvo/tzdb'
  import defaultTimezoneFromClientMixin from '../../helpers/default-timezone-from-client-mixin'

  export default {
    name: 'UserNotificationSettings',
    components: { Toggle, SettingTooltip },
    mixins: [defaultTimezoneFromClientMixin],
    props: {
      savedUserData: {
        type: Object,
        default: () => {},
      },
      notificationSettings: {
        type: Object,
        default () {
          return {
            globalReceiveEmail: false,
            globalReceivePush: false,
            globalReceiveSMS: false,
          }
        },
      },
    },
    data: () => ({
      globalReceiveEmail: false,
      globalReceivePush: false,
      globalReceiveSMS: false,
      phone: '',
      iso2: '',
      countryLoaded: false, // prevents validation on mount of previously saved phones without country codes
      phoneE164: '',
      phoneValid: null,
      phoneValidated: false,
      phoneIsMobile: null,
      showPhoneTip: false,
      timezone: { value: 'America/Los_Angeles', text: '' },
      needToInitUserTimezone: false,
    }),
    computed: {
      ...mapGetters({
        currentUser: 'Users/currentUser',
        currentSelectedOrg: 'Permissions/currentSelectedOrg',
        canAdminUser: 'Permissions/canAdminUser',
        isUserSelf: 'Permissions/isUserSelf',
        isInternal: 'Permissions/isInternal',
        savedUserTimezone: 'Users/userTimezone',
      }),
      timezones () {
        return getTimeZones().map(tz => ({ text: tz.rawFormat, value: tz.name }))
      },
      noSavedPhone () {
        return !(this.savedUserData.phone && this.savedUserData.phone.length)
      },
      orgId () {
        return this.currentSelectedOrg?.id
      },
      settingsEnabled () {
        const isSingleOrg = this.savedUserData.orgs?.length === 1
        return this.isUserSelf ||
          (this.isInternal && this.orgId) ||
          (this.canAdminUser(this.savedUserData, this.orgId) && isSingleOrg)
      },
      receiveSMSDisabled () {
        return !this.phoneE164.length ||
          !this.savedUserData.phone?.length ||
          !(this.phoneValidated && this.phoneValid)
      },
      phoneErrors () {
        if (this.phone && this.countryLoaded) {
          if (!this.iso2) return 'Please select a country'
          if (this.phoneValidated && !this.phoneValid) return 'Invalid Phone Number Format'
          if (this.phoneValidated && !this.phoneIsMobile) return 'Please enter a mobile number'
        }
        return ''
      },
      showPhoneCheck () {
        return this.phone && this.phoneValidated && this.phoneValid && this.phoneIsMobile
      },
      phoneChanged () {
        return this.savedUserData.phone !== this.phoneE164
      },
      phoneCleared () {
        return this.phoneChanged && this.phone === ''
      },
    },
    watch: {
      $route (to) {
        if (to.query.index) {
          this.initializePhone()
          this.setNotifications()
          this.rerenderContainer()
        }
      },
      notificationSettings () {
        this.setNotifications()
      },
      savedUserData ({ phone: newPhone, id: newId }, { id: oldId }) {
        if (newPhone !== this.phoneE164) this.initializePhone()
        if (newId !== undefined && newId !== oldId) this.initializeUserTimezone()
      },
    },
    async mounted () {
      // set data
      this.initializePhone()
      if (this.savedUserData.id) this.initializeUserTimezone()
    },
    methods: {
      async initializePhone () {
        this.phone = this.savedUserData?.phone ?? ''
        this.phoneE164 = this.phone
        this.countryLoaded = false
        if (this.phone) await this.validatePhone(this.phone)
      },
      setNotifications () {
        this.globalReceiveEmail = this.notificationSettings.globalReceiveEmail
        this.globalReceiveSMS = this.notificationSettings.globalReceiveSMS
        this.globalReceivePush = this.notificationSettings.globalReceivePush
      },
      updateCountry ({ iso2 }) {
        this.iso2 = iso2
        if (this.countryLoaded) this.handlePhoneBlur()
        else this.countryLoaded = true
      },
      handlePhoneInput (phone, { number }) {
        this.phoneValidated = false
        this.phone = phone
        if (number.e164) {
          this.phoneE164 = number.e164
        } else this.phoneE164 = ''
      },
      async handlePhoneBlur () {
        if (this.phoneE164 && this.phoneChanged) {
          this.validatePhone()
            .then(() => {
              if (this.phoneValid && this.phoneIsMobile) this.updatePhone(this.phoneE164)
            })
        } else if (this.phoneCleared) this.updatePhone('')
      },
      validatePhone (phone = this.phoneE164) {
        return validationAxios.post(`${process.env.VUE_APP_VALIDATOR_URL}/api/v1/validation/phone`, { phone })
          .then(({ data }) => {
            this.phoneIsMobile = data.isMobile
            this.phoneValid = true
            this.phoneValidated = true
          })
          .catch(() => {
            this.phoneValid = false
            this.phoneValidated = true
            throw new Error()
          })
      },
      updatePhone (phone) {
        return this.$store.dispatch('Users/updateUser', {
          userId: this.savedUserData.id,
          body: { phone },
        })
          .then((res) => {
            if (this.savedUserData.id === this.currentUser.id) {
              this.$store.commit('Users/setCurrentUserData', res.data.data)
            }
            this.$store.commit('Users/setUserData', res.data.data)
            this.$store.dispatch('updateMessages', { type: 'success', text: 'Phone number updated successfully' }, { root: true })
          })
          .catch(() => {
            this.$store.dispatch('updateMessages', { type: 'error', text: "We were unable to update this account's settings. Please try again." }, { root: true })
          })
      },
      checkSMS () {
        if (!this.savedUserData.phone?.length && !this.phoneE164.length) {
          this.$refs.phone.focus()
          this.showPhoneTip = true
          setTimeout(() => {
            this.showPhoneTip = false
          }, 3000)
        }
      },
      handleNotificationSettingsChange ({ updated, value }) {
        this.$store.dispatch('Users/updateUserNotificationSettings', {
          updated,
          value,
          userId: this.savedUserData.id,
          orgId: this.savedUserData.orgs[0].id,
        })
      },
      async initializeUserTimezone () {
        await this.$store.dispatch('Users/fetchUserTimezone', { userId: this.savedUserData.id })
        if (this.savedUserTimezone) {
          const matchedZone = this.timezones.find(tz => tz.value === this.savedUserTimezone)
          this.timezone = matchedZone || { value: this.savedUserTimezone, text: this.savedUserTimezone }
        } else {
          this.needToInitUserTimezone = true
          this.timezone = this.defaultTimezoneFromClientOffset
        }
      },
      async updateUserTimezone (timeZone) {
        const payload = { userId: this.savedUserData.id, orgs: this.savedUserData.orgs.map(o => o.id), userTimezone: timeZone.value }
        if (this.needToInitUserTimezone) {
          await this.$store.dispatch('Users/createUserTimezone', payload)
          this.needToInitUserTimezone = false
        } else {
          await this.$store.dispatch('Users/updateUserTimezone', payload)
        }
      },
      rerenderContainer () {
        this.containerKey++
      },
      handleCancel () {
        this.rerenderContainer()
      },
    },
  }
</script>

<style lang="scss">
#user-notification-settings {
    padding: 36px 36px 28px 36px;
}
h6.toggle-label {
    margin-top: -4px;
    margin-left: -10px;
    color: unset;
    display: inline-block;
}
.email-address-field {
    width: 322px;
    max-width: 322px;
    height: 44px;
    &.v-input--is-disabled {
        .v-input__slot:before {
            border:none;
        }
    }
    input {
        padding-bottom: 6px;
        margin-top: 14px;
    }
    .v-label {
        top: 12px;
    }
}
.phone-tip-container {
  color: darkgreen;
  padding-left: 5px;
  max-width: 205px;
  display: flex;
}
.country-code .v-input__slot {
  padding: 0 !important;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .5s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
